<template>
  <component
    :is="tag"
    class="notif-bell"
    :class="{
      'header__controls-item': tag === 'li',
      'notif-bell--mobile': mobile
    }"
    v-click-outside="close"
  >
    <button
      type="button"
      class="notif-bell__btn"
      :class="{ 'header__controls-link': !mobile, active: open }"
      @click="toggle"
      aria-label="התראות"
    >
      <span
        v-if="unreadCount > 0"
        class="
          header__controls-counter header__controls-counter--red
          notif-bell__counter
        "
        >{{ unreadCount > 99 ? '99+' : unreadCount }}</span
      >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"
          stroke="#5A6F95"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M13.73 21a2 2 0 0 1-3.46 0"
          stroke="#5A6F95"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <div v-if="!mobile" class="tooltip bot">התראות</div>

    <div v-if="open" class="notif-bell__pop" dir="rtl">
      <div class="notif-bell__head">
        <span class="notif-bell__title">התראות</span>
        <button
          v-if="unreadCount > 0"
          type="button"
          class="notif-bell__readall"
          @click="markAllRead"
        >
          סמן הכל כנקרא
        </button>
      </div>

      <div v-if="loading" class="notif-bell__state">טוען...</div>
      <div v-else-if="!items.length" class="notif-bell__state">אין התראות</div>
      <ul v-else class="notif-bell__list">
        <li
          v-for="n in items"
          :key="n.id"
          class="notif-bell__item"
          :class="{ 'is-unread': !n.read_at }"
          @click="onClickItem(n)"
        >
          <div class="notif-bell__item-title">{{ n.title }}</div>
          <div v-if="n.body" class="notif-bell__item-body">{{ n.body }}</div>
          <div class="notif-bell__item-time">{{ relTime(n.created_at) }}</div>
        </li>
      </ul>
    </div>
  </component>
</template>

<script>
import axios from 'axios';

const POLL_MS = 60000;

export default {
  name: 'NotificationBell',
  props: {
    // 'li' for the desktop header__controls list, 'div' for the mobile group
    tag: { type: String, default: 'li' },
    mobile: { type: Boolean, default: false }
  },
  data() {
    return {
      open: false,
      loading: false,
      items: [],
      unreadCount: 0,
      pollTimer: null
    };
  },
  mounted() {
    this.fetchUnreadCount();
    this.pollTimer = setInterval(this.fetchUnreadCount, POLL_MS);
  },
  beforeDestroy() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
    }
  },
  methods: {
    async fetchUnreadCount() {
      try {
        const { data } = await axios.get('/api/notifications/unread-count');
        this.unreadCount = data?.unread_count ?? 0;
      } catch (e) {
        // ignore — header bell should never break the page
      }
    },
    async fetchList() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/notifications', {
          params: { per_page: 20 }
        });
        this.items = data?.data || [];
        if (typeof data?.unread_count === 'number') {
          this.unreadCount = data.unread_count;
        }
      } catch (e) {
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    toggle() {
      this.open = !this.open;
      if (this.open) {
        this.fetchList();
      }
    },
    close() {
      this.open = false;
    },
    async markRead(n) {
      if (n.read_at) {
        return;
      }
      try {
        const { data } = await axios.post(`/api/notifications/${n.id}/read`);
        n.read_at = new Date().toISOString();
        if (typeof data?.unread_count === 'number') {
          this.unreadCount = data.unread_count;
        }
      } catch (e) {
        // ignore
      }
    },
    async markAllRead() {
      try {
        await axios.post('/api/notifications/read-all');
        this.unreadCount = 0;
        this.items = this.items.map(n => ({
          ...n,
          read_at: n.read_at || new Date().toISOString()
        }));
      } catch (e) {
        // ignore
      }
    },
    async onClickItem(n) {
      await this.markRead(n);
      if (n.link) {
        this.close();
        if (this.$route.path !== n.link) {
          this.$router.push(n.link).catch(() => {});
        }
      }
    },
    relTime(value) {
      if (!value) {
        return '';
      }
      const d = new Date(value);
      if (isNaN(d.getTime())) {
        return String(value);
      }
      const diff = Date.now() - d.getTime();
      if (diff < 60000) {
        return 'עכשיו';
      }
      const m = Math.floor(diff / 60000);
      if (m < 60) {
        return `לפני ${m} ד׳`;
      }
      const h = Math.floor(m / 60);
      if (h < 24) {
        return `לפני ${h} ש׳`;
      }
      const dd = Math.floor(h / 24);
      if (dd < 30) {
        return `לפני ${dd} י׳`;
      }
      return d.toLocaleDateString('he-IL');
    }
  }
};
</script>

<style lang="scss" scoped>
.notif-bell {
  position: relative;
}
.notif-bell--mobile {
  display: flex;
  align-items: center;
}
.notif-bell__btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  position: relative;

  &.header__controls-link {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: 0.3s;
    width: 24px;
    height: 24px;
    flex-shrink: 0;

    svg path {
      stroke: #5a6f95;
      stroke-width: 2;
      transition: stroke 0.3s;
    }

    &:hover {
      background-color: #e5f2ff;

      svg path {
        stroke: #409fff;
      }
    }

    &.active {
      background-color: #e5f2ff;

      svg path {
        stroke: #409fff;
      }
    }
  }
}
.notif-bell--mobile .notif-bell__btn {
  width: 36px;
  height: 36px;
  justify-content: center;
}
.notif-bell--mobile .notif-bell__counter {
  position: absolute;
  top: -2px;
  right: -2px;
}
.notif-bell__counter {
  min-width: 16px;
  height: 16px;
  line-height: 16px;
  font-size: 10px;
  text-align: center;
  padding: 0 3px;
}
.notif-bell__pop {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 340px;
  max-width: 90vw;
  background: #fff;
  border: 1px solid #e3e7ee;
  border-radius: 12px;
  box-shadow: 0 16px 32px -10px rgba(15, 18, 32, 0.22);
  z-index: 200;
  overflow: hidden;
  text-align: right;
}
.notif-bell__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid #eef1f6;
}
.notif-bell__title {
  font-weight: 700;
  font-size: 14px;
  color: #38455d;
}
.notif-bell__readall {
  border: none;
  background: transparent;
  color: #409fff;
  font: inherit;
  font-size: 12px;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}
.notif-bell__state {
  padding: 28px 14px;
  text-align: center;
  color: #5a6f95;
  font-size: 13px;
}
.notif-bell__list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 360px;
  overflow-y: auto;
}
.notif-bell__item {
  padding: 10px 14px;
  border-bottom: 1px solid #f1f4f8;
  cursor: pointer;
  transition: background 0.12s;
  &:hover {
    background: #f6f8fc;
  }
  &.is-unread {
    background: #eef5ff;
    &:hover {
      background: #e4eeff;
    }
  }
}
.notif-bell__item-title {
  font-size: 13px;
  font-weight: 600;
  color: #38455d;
  text-align: right;
}
.notif-bell__item-body {
  margin-top: 2px;
  font-size: 12px;
  color: #5a6f95;
  text-align: right;
}
.notif-bell__item-time {
  margin-top: 4px;
  font-size: 11px;
  color: #9aa7bd;
  text-align: right;
}
</style>
